import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Plus, Calendar as CalendarIcon, Trash2, Edit, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO, parse } from 'date-fns';
import { cs } from 'date-fns/locale';

export const SensoryTeamSchedulePage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';
  const basePath = `/senzorika/${sensoryType}`;

  useEffect(() => {
    fetchData();
  }, [sensoryType]);

  const fetchData = async () => {
    try {
      const [schedulesRes, membersRes] = await Promise.all([
        api.get('/sensory-team-schedule', { params: { sensory_type: sensoryType } }),
        api.get('/sensory-team', { params: { sensory_type: sensoryType } })
      ]);
      setSchedules(schedulesRes.data);
      setTeamMembers(membersRes.data);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (schedule = null) => {
    if (schedule && schedule.date) {
      // Edit mode - convert date from yyyy-MM-dd to dd.MM.yyyy
      const parts = schedule.date.split('-');
      const formattedDate = `${parts[2]}.${parts[1]}.${parts[0]}`;
      setSelectedDate(formattedDate);
      setSelectedMembers(schedule.member_ids || []);
      setEditingSchedule(schedule);
    } else {
      // New mode
      setSelectedDate('');
      setSelectedMembers([]);
      setEditingSchedule(null);
    }
    setCalendarOpen(false);
    setDialogOpen(true);
  };

  const handleDateChange = (dateStr) => {
    setSelectedDate(dateStr || '');
  };

  // Parse selected date for Calendar component
  const parsedDate = React.useMemo(() => {
    if (!selectedDate) return undefined;
    try {
      const parsed = parse(selectedDate, 'dd.MM.yyyy', new Date());
      if (!isNaN(parsed.getTime())) return parsed;
    } catch {}
    return undefined;
  }, [selectedDate]);

  const handleCalendarSelect = (date) => {
    if (date) {
      const formatted = format(date, 'dd.MM.yyyy');
      setSelectedDate(formatted);
    }
    setCalendarOpen(false);
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error('Vyberte datum');
      return;
    }
    if (selectedMembers.length === 0) {
      toast.error('Vyberte alespoň jednoho člena týmu');
      return;
    }

    // Convert date from dd.MM.yyyy to yyyy-MM-dd
    let dateForApi = selectedDate;
    try {
      const parts = selectedDate.split('.');
      if (parts.length === 3) {
        dateForApi = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    } catch {}

    setSubmitting(true);
    try {
      await api.post('/sensory-team-schedule', {
        date: dateForApi,
        member_ids: selectedMembers,
        sensory_type: sensoryType
      });
      toast.success('Rozpis uložen');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Opravdu chcete smazat tento rozpis?')) return;
    
    try {
      await api.delete(`/sensory-team-schedule/${scheduleId}`);
      toast.success('Rozpis smazán');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  const formatDisplayDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'd. MMMM yyyy', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="p-8" data-testid="sensory-team-schedule-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(basePath)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">Rozpis senzorického týmu - {typeLabel}</h1>
            <p className="text-slate-600">Určete, kdo má hodnotit v který den</p>
          </div>
          <Button 
            onClick={handleOpenDialog}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nový rozpis
          </Button>
        </div>

        {/* Schedule List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
              Načítání...
            </div>
          ) : schedules.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                <CalendarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Žádné rozpisy</h3>
              <p className="text-slate-500 mb-4">Zatím nemáte žádné rozpisy hodnotitelů</p>
              <Button onClick={handleOpenDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Vytvořit první rozpis
              </Button>
            </div>
          ) : (
            schedules.map((schedule) => (
              <div 
                key={schedule.id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center text-white">
                      <span className="text-lg font-bold leading-none">
                        {format(parseISO(schedule.date), 'd')}
                      </span>
                      <span className="text-xs uppercase">
                        {format(parseISO(schedule.date), 'MMM', { locale: cs })}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">
                        {formatDisplayDate(schedule.date)}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {schedule.members?.map((member) => (
                          <span
                            key={member.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium"
                          >
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-semibold">
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </div>
                            {member.first_name} {member.last_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(schedule)}
                      className="text-slate-400 hover:text-purple-500"
                    >
                      <Edit className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(schedule.id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSchedule ? 'Upravit rozpis' : 'Nový rozpis'}</DialogTitle>
              <DialogDescription>
                Vyberte datum a členy týmu, kteří mají v daný den hodnotit
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Date picker - inline calendar */}
              <div className="space-y-2">
                <Label>Den zkoušky *</Label>
                <Button
                  variant="outline"
                  onClick={() => setCalendarOpen(!calendarOpen)}
                  className="w-full justify-between text-left font-normal"
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {selectedDate || 'Vyberte datum...'}
                  </span>
                  {calendarOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                {calendarOpen && (
                  <div className="border rounded-lg p-3 bg-white shadow-sm">
                    <Calendar
                      mode="single"
                      selected={parsedDate}
                      onSelect={handleCalendarSelect}
                      locale={cs}
                      className="rounded-md"
                    />
                  </div>
                )}
              </div>
              
              {/* Team members checkboxes */}
              <div className="space-y-2">
                <Label>Senzorický tým *</Label>
                {teamMembers.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    Nejsou definováni žádní členové týmu. 
                    <Button variant="link" className="px-1" onClick={() => navigate('/admin/sensory-team')}>
                      Přidejte je v administraci.
                    </Button>
                  </p>
                ) : (
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                    {teamMembers.map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedMembers.includes(member.id)}
                          onCheckedChange={() => handleMemberToggle(member.id)}
                        />
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-semibold">
                          {member.first_name[0]}{member.last_name[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !selectedDate || selectedMembers.length === 0}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                {submitting ? 'Ukládám...' : (editingSchedule ? 'Uložit změny' : 'Vytvořit rozpis')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SensoryTeamSchedulePage;
